import React, { useState, useEffect } from "react";
import { Sparkles, Plus, Play, Pause, Settings, MessageSquare, Users, Calendar, Zap } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import AppLayout from "@/components/AppLayout";

export default function AIWorkers() {
  const [campaigns, setCampaigns] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [campaignsRes, statsRes] = await Promise.all([
        api.get("/api/ai-workers/campaigns"),
        api.get("/api/ai-workers/stats"),
      ]);
      setCampaigns(campaignsRes.data.campaigns || []);
      setStats(statsRes.data);
    } catch (e) {
      toast.error("Failed to load AI workers data");
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const runCampaign = async (campaign) => {
    try {
      toast.info("Drafting campaign messages...");
      const res = await api.post("/api/ai-workers/send-messages", {
        campaign_id: campaign.id,
        platform: campaign.platform,
        dry_run: false,
      });
      // Server never delivers these: status is "draft only, not sent".
      toast.info(`${res.data.messages_drafted ?? 0} messages drafted — draft only, not sent`);
      loadData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to run campaign");
    }
  };

  return (
    <AppLayout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <Sparkles className="text-blue-400" size={32} />
              AI Workers
            </h1>
            <p className="text-zinc-400 mt-1">Automated messaging for OnlyFans, JustForFans, Members & VIP</p>
            <p data-testid="ai-workers-draft-notice" className="text-amber-300 text-sm mt-1">Draft only, not sent: running a campaign drafts messages; nothing is delivered to fans yet.</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg flex items-center gap-2 font-medium transition"
          >
            <Plus size={18} />
            New Campaign
          </button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <StatCard icon={Zap} label="Total Campaigns" value={stats.total_campaigns} color="#3b82f6" />
            <StatCard icon={Play} label="Active Campaigns" value={stats.active_campaigns} color="#10b981" />
            <StatCard icon={MessageSquare} label="Recent Runs" value={stats.recent_runs?.length || 0} color="#8b5cf6" />
          </div>
        )}

        {/* Campaigns List */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-lg overflow-hidden">
          <div className="p-4 border-b border-zinc-800">
            <h2 className="text-lg font-semibold">Message Campaigns</h2>
          </div>

          {loading ? (
            <div className="p-8 text-center text-zinc-400">Loading campaigns...</div>
          ) : campaigns.length === 0 ? (
            <div className="p-8 text-center">
              <Sparkles size={48} className="mx-auto mb-4 text-zinc-600" />
              <p className="text-zinc-400 mb-4">No campaigns yet. Create your first AI-powered messaging campaign!</p>
              <button
                onClick={() => setShowCreateModal(true)}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg inline-flex items-center gap-2"
              >
                <Plus size={16} />
                Create Campaign
              </button>
            </div>
          ) : (
            <div className="divide-y divide-zinc-800">
              {campaigns.map((camp) => (
                <CampaignRow key={camp.id} campaign={camp} onRun={runCampaign} />
              ))}
            </div>
          )}
        </div>

        {/* Create Modal */}
        {showCreateModal && <CreateCampaignModal onClose={() => setShowCreateModal(false)} onSuccess={loadData} />}
      </div>
    </AppLayout>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4">
      <div className="flex items-center gap-3 mb-2">
        <div className="p-2 rounded" style={{ background: `${color}20` }}>
          <Icon size={20} style={{ color }} />
        </div>
        <span className="text-sm text-zinc-400">{label}</span>
      </div>
      <p className="text-2xl font-bold">{value}</p>
    </div>
  );
}

function CampaignRow({ campaign, onRun }) {
  return (
    <div className="p-4 hover:bg-zinc-800/50 transition">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-1">
            <h3 className="font-semibold">{campaign.campaign_name}</h3>
            <span className="px-2 py-0.5 bg-zinc-800 rounded text-xs text-zinc-400">
              {campaign.platform}
            </span>
            <span className={`px-2 py-0.5 rounded text-xs ${campaign.enabled ? 'bg-green-900/30 text-green-400' : 'bg-zinc-800 text-zinc-500'}`}>
              {campaign.enabled ? 'Active' : 'Paused'}
            </span>
          </div>
          <div className="flex items-center gap-4 text-sm text-zinc-400">
            <span className="flex items-center gap-1">
              <MessageSquare size={14} />
              {campaign.message_type}
            </span>
            <span className="flex items-center gap-1">
              <Users size={14} />
              {campaign.target_audience}
            </span>
            <span className="flex items-center gap-1">
              <Calendar size={14} />
              {campaign.trigger_type === 'cron' ? campaign.cron_schedule : campaign.trigger_type}
            </span>
          </div>
          {campaign.last_run && (
            <p className="text-xs text-zinc-500 mt-1">
              Last run: {new Date(campaign.last_run).toLocaleString()}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => onRun(campaign)}
            title="Draft only, not sent"
            className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 rounded text-sm flex items-center gap-1 transition"
          >
            <Play size={14} />
            Draft Now
          </button>
        </div>
      </div>
    </div>
  );
}

function CreateCampaignModal({ onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    campaign_name: "",
    platform: "onlyfans",
    message_type: "welcome",
    trigger_type: "manual",
    target_audience: "all_subs",
    ai_tone: "friendly",
    enabled: true,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/api/ai-workers/campaigns", {
        ...formData,
        id: `camp_${Date.now()}`,
      });
      toast.success("Campaign created!");
      onSuccess();
      onClose();
    } catch (e) {
      toast.error("Failed to create campaign");
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-zinc-900 border border-zinc-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-zinc-800 flex items-center justify-between">
          <h2 className="text-xl font-bold">Create AI Campaign</h2>
          <button onClick={onClose} className="text-zinc-400 hover:text-white">×</button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Campaign Name</label>
            <input
              type="text"
              value={formData.campaign_name}
              onChange={(e) => setFormData({ ...formData, campaign_name: e.target.value })}
              className="w-full bg-zinc-950 border border-zinc-700 rounded px-3 py-2"
              placeholder="e.g., Welcome New Subscribers"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Platform</label>
              <select
                value={formData.platform}
                onChange={(e) => setFormData({ ...formData, platform: e.target.value })}
                className="w-full bg-zinc-950 border border-zinc-700 rounded px-3 py-2"
              >
                <option value="onlyfans">OnlyFans</option>
                <option value="justforfans">JustForFans</option>
                <option value="members">Members</option>
                <option value="vip">VIP</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Message Type</label>
              <select
                value={formData.message_type}
                onChange={(e) => setFormData({ ...formData, message_type: e.target.value })}
                className="w-full bg-zinc-950 border border-zinc-700 rounded px-3 py-2"
              >
                <option value="welcome">Welcome</option>
                <option value="engagement">Re-engagement</option>
                <option value="upsell">PPV Upsell</option>
                <option value="custom">Custom</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Trigger Type</label>
              <select
                value={formData.trigger_type}
                onChange={(e) => setFormData({ ...formData, trigger_type: e.target.value })}
                className="w-full bg-zinc-950 border border-zinc-700 rounded px-3 py-2"
              >
                <option value="manual">Manual</option>
                <option value="cron">Scheduled (Cron)</option>
                <option value="webhook">Webhook (Coming Soon)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Target Audience</label>
              <select
                value={formData.target_audience}
                onChange={(e) => setFormData({ ...formData, target_audience: e.target.value })}
                className="w-full bg-zinc-950 border border-zinc-700 rounded px-3 py-2"
              >
                <option value="all_subs">All Subscribers</option>
                <option value="new_subs">New Subscribers (7d)</option>
                <option value="top_spenders">Top Spenders</option>
                <option value="inactive_30d">Inactive 30 Days</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">AI Tone</label>
            <select
              value={formData.ai_tone}
              onChange={(e) => setFormData({ ...formData, ai_tone: e.target.value })}
              className="w-full bg-zinc-950 border border-zinc-700 rounded px-3 py-2"
            >
              <option value="friendly">Friendly</option>
              <option value="flirty">Flirty</option>
              <option value="professional">Professional</option>
              <option value="playful">Playful</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={formData.enabled}
              onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
              className="w-4 h-4"
            />
            <label className="text-sm">Enable campaign immediately</label>
          </div>

          <div className="flex gap-2 pt-4">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 rounded">
              Cancel
            </button>
            <button type="submit" className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded">
              Create Campaign
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
